import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { hasAnyGrant } from "../helpers/permissions";
import type { Project } from "../api-models";

interface ProjectLinkProps {
  /** The related project as preloaded with the document — carries both id and name. */
  project?: Project | null;
  /** Report rows carry the project as flat columns instead of a relation. */
  id?: number;
  name?: string;
  /** Rendered when the document has no project at all (it is optional on some). */
  fallback?: ReactNode;
}

/**
 * A project name is never a dead end: wherever one is shown it links to the project.
 * Falls back to plain text when the id is unknown or the user cannot read projects, and
 * swallows the click so a project inside a clickable table row doesn't also open the row.
 */
export function ProjectLink({ project, id, name, fallback = null }: ProjectLinkProps) {
  const { user } = useUser();
  const projectId = project?.id ?? id;
  const label = project?.name ?? name;

  if (!label) return <>{fallback}</>;
  if (!projectId || !hasAnyGrant(user, "Projects.Read")) return <>{label}</>;
  return (
    <Link to={`/projects/${projectId}`} onClick={(e) => e.stopPropagation()}>
      {label}
    </Link>
  );
}

// A link has no text of its own to walk in a CSV export (helpers/csv.ts) — the name is it.
ProjectLink.csvText = ({ project, name, fallback }: ProjectLinkProps) => project?.name ?? name ?? fallback;
